import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Form, Modal, Button, Row, Col } from "react-bootstrap";
import { useTranslate } from "../hooks/LanguageProvider";
import { IoEye, IoEyeOff } from "react-icons/io5";
import { Bounce, toast, ToastOptions } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface UserOption {
  label: string; // Afficher le nom et le prénom de l'utilisateur
  value: string; // ID de l'utilisateur
  nom: string; // Nom de l'utilisateur
  prenom: string; // Prénom de l'utilisateur
}

interface User {
  id_user: string;
  nom_user: string;
  company: string;
  prenom_user: string;
  nom_role: string;
  id_role: string;

  // ... autres champs
}

interface RequestUpdateBody {
  first_name: string;
  middle_name: string;
  company: string;
  phone: string;
  email: string;
  username: string;
  passwd: string;
  userM: string | null;
  roleUser: string;
  wilaya: string;
  validite: string;
  manager?: string; // Ajoutez cette ligne
  changePassword: boolean; // Ajoutez cette ligne
}
interface RequestBody {
  first_name: string;
  middle_name: string;
  company: string;
  phone: string;
  email: string;
  username: string;
  passwd: string;
  userM: string | null;
  roleUser: string;
  wilaya: string;
  validite: string;
  manager?: string; // Ajoutez cette ligne
}

const User: React.FC = () => {
  const [userData, setUserData] = useState({
    nom: "",
    prenom: "",
    company: "",
    telephone: "",
    email: "",
    username: "",
    motDePasse: "",
    wilaya: "",
    affecteA: "",
    roles: "",
    valideJusqua: "",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const user_id = localStorage.getItem("userid");
  const [affecteAOptions, setAffecteAOptions] = useState<UserOption[]>([]);
  const [rolesOptions] = useState<UserOption[]>([
    { label: "Admin", value: "1", nom: "", prenom: "" },
    { label: "Supplier", value: "2", nom: "", prenom: "" },
    { label: "OEM", value: "3", nom: "", prenom: "" },
  ]);
  const [userExists, setUserExists] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmationMotDePasse, setConfirmationMotDePasse] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { translate } = useTranslate();
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [requiredFieldError, setRequiredFieldError] = useState<string>("");
  const [passwordMismatchError, setPasswordMismatchError] =
    useState<string>("");
  const [passwordValidationError, setPasswordValidationError] =
    useState<string>("");

  // const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [passwordLength, setPasswordLength] = useState(8); // Longueur par défaut du mot de passe
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [selectedWilaya, setSelectedWilaya] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const { userurlID } = useParams<{ userurlID?: string }>();
  console.log(userurlID);

  const isEditMode = !!userurlID;
  const searchParams = new URLSearchParams(location.search);
  const requestedRole = searchParams.get("role");
  const returnTo = searchParams.get("from");
  useEffect(() => {
    const fetchUserData = async () => {
      if (isEditMode) {
        try {
          const response = await fetch(
            `${backendUrl}/api/userform/${userurlID}`,
          );

          if (!response.ok) {
            throw new Error(`Failed to load user. Status: ${response.status}`);
          }

          const data = await response.json();

          setUserData({
            nom: data?.userDetails?.nom_user || "",
            prenom: data?.userDetails?.prenom_user || "",
            company: data?.userDetails?.company || "",
            telephone: data?.userDetails?.phone_user || "",
            email: data?.userDetails?.email_user || "",
            username: data?.userDetails?.username_user || "",
            motDePasse: "",
            wilaya: data?.userDetails?.wilaya || "",
            affecteA: String(data?.manageduser_id?.id_user || ""),
            roles: String(data?.userDetails?.id_role || ""),
            valideJusqua: data?.userDetails?.validite || "",
          });

          if (data?.userDetails?.wilaya) {
            setSelectedWilaya({
              value: data.userDetails.wilaya,
              label: data.userDetails.wilaya,
            });
          } else {
            setSelectedWilaya(null);
          }

          const managedUserId = String(data?.manageduser_id?.id_user || "");

          if (managedUserId) {
            const selectedUserOption = affecteAOptions.find(
              (option) => String(option.value) === managedUserId,
            );
            setSelectedUser(selectedUserOption || null);
          } else {
            setSelectedUser(null);
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des détails de l'utilisateur :",
            error,
          );
        }
      }
    };

    fetchUserData();
  }, [isEditMode, userurlID, affecteAOptions]);
  useEffect(() => {
    if (isEditMode) return;
    if (!requestedRole) return;
    if (rolesOptions.length === 0) return;

    const normalizedRequestedRole = requestedRole.trim().toLowerCase();

    const matchedRole = rolesOptions.find(
      (role) => role.label.trim().toLowerCase() === normalizedRequestedRole,
    );

    if (matchedRole) {
      setUserData((prev) => ({
        ...prev,
        roles: matchedRole.value,
      }));
    }
  }, [requestedRole, rolesOptions, isEditMode]);
  useEffect(() => {
    const fetchAffecteAOptions = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/users/find/${user_id}`);
        const data = await response.json();

        if (Array.isArray(data)) {
          setAffecteAOptions(
            data.map((user: User) => ({
              label: `${user.nom_user} ${user.prenom_user}`,
              value: user.id_user,
              nom: user.nom_user,
              prenom: user.prenom_user,
            })),
          );
        } else {
          console.error("La réponse de l'API n'est pas un tableau :", data);
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des options du sélecteur",
          error,
        );
      }
    };

    fetchAffecteAOptions();
  }, [user_id]);
  /*  useEffect(() => {
    if (!isSupplierFlow) return;
    if (affecteAOptions.length === 0) return;
    if (!user_id) return;

    const currentUserOption = affecteAOptions.find(
      (option) => String(option.value) === String(user_id),
    );

    if (currentUserOption) {
      setSelectedUser(currentUserOption);
    }
  }, [isSupplierFlow, affecteAOptions, user_id]);*/
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };
  const handleUsernametChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newUsername = e.target.value;
    setUserData({
      ...userData,
      username: newUsername,
    });

    try {
      if (newUsername !== "") {
        // Check if in edit mode and the new username is the same as the current user's username
        if (isEditMode && newUsername === userData.username) {
          // If it's the same username in edit mode, set userExists to false and return
          setUserExists(false);
          return;
        }

        const response = await fetch(`${backendUrl}/api/verifierUtilisateur`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ thisusername: newUsername }),
        });

        if (!response.ok) {
          console.error("Error during API request:", response.statusText);
          return;
        }

        const data = await response.json();

        // Check if the username already exists, excluding the current user's username
        if (data.exist && newUsername !== userData.username) {
          setUserExists(true);
        } else {
          setUserExists(false);
        }
      } else {
        // If the username field is empty, reset userExists to false
        setUserExists(false);
      }
    } catch (error) {
      console.error("Error during API request:", error);
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  // Dans votre composant
  const handleUserChange = (selectedOption: UserOption | null) => {
    setSelectedUser(selectedOption); // Stockez l'objet complet de l'utilisateur
  };

  const handleWilayaChange = (
    selectedOption: { value: string; label: string } | null,
  ) => {
    setSelectedWilaya(
      selectedOption
        ? { value: selectedOption.value, label: selectedOption.label }
        : null,
    );
  };

  const wilayasOptions = [
    { value: "Adrar", label: "Adrar" },
    { value: "Chlef", label: "Chlef" },
    { value: "Laghouat", label: "Laghouat" },
    { value: "Oum El Bouaghi", label: "Oum El Bouaghi" },
    { value: "Batna", label: "Batna" },
    { value: "Béjaïa", label: "Béjaïa" },
    { value: "Biskra", label: "Biskra" },
    { value: "Béchar", label: "Béchar" },
    { value: "Blida", label: "Blida" },
    { value: "Bouira", label: "Bouira" },
    { value: "Tamanrasset", label: "Tamanrasset" },
    { value: "Tébessa", label: "Tébessa" },
    { value: "Tlemcen", label: "Tlemcen" },
    { value: "Tiaret", label: "Tiaret" },
    { value: "Tizi Ouzou", label: "Tizi Ouzou" },
    { value: "Alger", label: "Alger" },
    { value: "Djelfa", label: "Djelfa" },
    { value: "Jijel", label: "Jijel" },
    { value: "Sétif", label: "Sétif" },
    { value: "Saïda", label: "Saïda" },
    { value: "Skikda", label: "Skikda" },
    { value: "Sidi Bel Abbès", label: "Sidi Bel Abbès" },
    { value: "Annaba", label: "Annaba" },
    { value: "Guelma", label: "Guelma" },
    { value: "Constantine", label: "Constantine" },
    { value: "Médéa", label: "Médéa" },
    { value: "Mostaganem", label: "Mostaganem" },
    { value: "M'Sila", label: "M'Sila" },
    { value: "Mascara", label: "Mascara" },
    { value: "Ouargla", label: "Ouargla" },
    { value: "Oran", label: "Oran" },
    { value: "El Bayadh", label: "El Bayadh" },
    { value: "Illizi", label: "Illizi" },
    { value: "Bordj Bou Arréridj", label: "Bordj Bou Arréridj" },
    { value: "Boumerdès", label: "Boumerdès" },
    { value: "El Tarf", label: "El Tarf" },
    { value: "Tindouf", label: "Tindouf" },
    { value: "Tissemsilt", label: "Tissemsilt" },
    { value: "El Oued", label: "El Oued" },
    { value: "Khenchela", label: "Khenchela" },
    { value: "Souk Ahras", label: "Souk Ahras" },
    { value: "Tipaza", label: "Tipaza" },
    { value: "Mila", label: "Mila" },
    { value: "Aïn Defla", label: "Aïn Defla" },
    { value: "Naâma", label: "Naâma" },
    { value: "Aïn Témouchent", label: "Aïn Témouchent" },
    { value: "Ghardaïa", label: "Ghardaïa" },
    { value: "Relizane", label: "Relizane" },
  ];

  const handleCancel = () => {
    // Annuler l'ajout et revenir à la page des utilisateurs
    setRequiredFieldError("");
    setPasswordMismatchError("");
    setPasswordValidationError("");
    setShowConfirmationModal(false);
    navigate("/users");
  };

  const handleAddUser = async () => {
    try {
      const requestBody: RequestBody = {
        first_name: userData.nom,
        middle_name: userData.prenom,
        company: userData.company,
        passwd: userData.motDePasse,
        email: userData.email,
        username: userData.username,
        wilaya: selectedWilaya ? selectedWilaya.value : "",
        phone: userData.telephone,
        userM: selectedUser ? selectedUser.value : null,
        roleUser: userData.roles,
        validite: userData.valideJusqua,
      };

      const response = await fetch(`${backendUrl}/api/addUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data?.message || "Failed to create user");
        console.error("Add user failed:", data);
        return false;
      }

      toast.success(
        isEditMode
          ? translate("Utilisateur modifié avec succès")
          : translate("Utilisateur ajouté avec succès"),
        {
          position: "bottom-right",
          autoClose: 2400,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        },
      );

      return true;
    } catch (error) {
      console.error("Erreur lors de l'appel de l'API", error);
      toast.error("Server error while creating user");
      return false;
    }
  };

  const handleUpdateUser = async () => {
    try {
      const requestBody: RequestUpdateBody = {
        first_name: userData.nom,
        middle_name: userData.prenom,
        company: userData.company,
        passwd: userData.motDePasse,
        email: userData.email,
        username: userData.username,
        wilaya: selectedWilaya ? selectedWilaya.value : "",
        phone: userData.telephone,
        userM: selectedUser ? selectedUser.value : null,
        roleUser: userData.roles,
        validite: userData.valideJusqua,
        changePassword: changePassword, // Ajout de changePassword
      };

      const response = await fetch(`${backendUrl}/api/updateUser`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userurlID,
          ...requestBody,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          isEditMode
            ? translate("Utilisateur modifié avec succès")
            : translate("Utilisateur ajouté avec succès"),
          {
            position: "bottom-right",
            autoClose: 2400,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          },
        );
      } else {
        console.error("Échec de la requête API", response.statusText);
      }
    } catch (error) {
      console.error("Erreur lors de l'appel de l'API", error);
    }

    try {
      const timestamp = new Date(Date.now() + 60 * 60 * 1000)
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const details = `Update user: ${userData.nom}`;

      await fetch(`${backendUrl}/api/log-user-action/${user_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "update",
          page: "Users",
          operation: "modification",
          timestamp,
          details,
        }),
      });
    } catch (error) {
      console.error("Erreur de journalisation de l'action utilisateur:", error);
    }
  };

  // open modal for user action
  const handleOpenConfirmationModal = () => {
    // Réinitialiser les erreurs
    setRequiredFieldError("");
    setPasswordMismatchError("");
    setPasswordValidationError("");
    setUserExists(false);

    // Vérifiez si tous les champs requis sont remplis
    if (
      !userData.nom ||
      !userData.prenom ||
      !userData.username ||
      !selectedWilaya ||
      !userData.roles ||
      (!userData.motDePasse && isEditMode && changePassword) ||
      (userData.motDePasse !== confirmationMotDePasse && changePassword) ||
      (!isPasswordValid(userData.motDePasse) && changePassword) ||
      userData.roles === "" ||
      userData.roles === "0"
    ) {
      // Ajouter un message d'erreur pour les champs requis manquants ou les erreurs de mot de passe
      setRequiredFieldError(
        translate("Veuillez remplir tous les champs obligatoires"),
      );
      if (!isPasswordValid(userData.motDePasse) && changePassword) {
        // Ajouter un message d'erreur spécifique si le mot de passe n'est pas valide
        setPasswordValidationError(
          translate(
            "Le mot de passe doit contenir au moins une minuscule, une majuscule, un chiffre, un caractère spécial et faire au moins 8 caractères de long",
          ),
        );
      }
      if (userData.motDePasse !== confirmationMotDePasse && changePassword) {
        // Ajouter un message d'erreur pour les mots de passe non correspondants
        setPasswordMismatchError(
          translate("Les mots de passe ne correspondent pas"),
        );
      }
      return; // Ajoutez un return pour éviter d'ouvrir le modal si des champs requis sont manquants ou si le mot de passe est invalide
    }

    // Vérifiez si le nom d'utilisateur existe déjà
    if (userExists) {
      // Ajouter un message d'erreur pour le nom d'utilisateur existant
      setUserExists(true);
      return; // Ajoutez un return pour éviter d'ouvrir le modal si le nom d'utilisateur existe déjà
    }

    // Ouvrez la modal de confirmation si aucune erreur n'est détectée
    setShowConfirmationModal(true);
  };

  // confirm button for user action add and modify
  const handleConfirmUpdate = async () => {
    setShowConfirmationModal(false);

    if (isEditMode) {
      await handleUpdateUser();
      if (returnTo === "Experts" || userData.roles === "4") {
        navigate("/Experts");
        return;
      }
      navigate("/users");
      return;
    }

    const success = await handleAddUser();

    if (!success) return;

    if (requestedRole?.toLowerCase() === "supplier") {
      navigate("/Suppliers");
      return;
    }

    if (requestedRole?.toLowerCase() === "oem") {
      navigate("/OEMs");
      return;
    }

    navigate("/users");
  };

  // Define regular expressions for password validation
  const lower = new RegExp("(?=.*[a-z])");
  const upper = new RegExp("(?=.*[A-Z])");
  const number = new RegExp("(?=.*[0-9])");
  const special = new RegExp("(?=.*[!@#\\$%^&*()\\[\\]\"';,:?/+~`|_-]+)");
  const length = new RegExp("(?=.{8,})");

  // Function to check if password meets all criteria
  const isPasswordValid = (password: string) => {
    return (
      lower.test(password) &&
      upper.test(password) &&
      number.test(password) &&
      special.test(password) &&
      length.test(password)
    );
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    const newErrors = [];

    // Vérifier si le champ de mot de passe est vide
    if (password.trim() === "") {
      // Si le champ de mot de passe est vide, effacer les erreurs
      setPasswordErrors([]);
    } else {
      // Vos vérifications d'erreurs ici...
      if (!lower.test(password)) {
        newErrors.push(
          translate("Le mot de passe doit contenir au moins une minuscule"),
        );
      }
      if (!upper.test(password)) {
        newErrors.push(
          translate("Le mot de passe doit contenir au moins une majuscule"),
        );
      }
      if (!number.test(password)) {
        newErrors.push(
          translate("Le mot de passe doit contenir au moins un chiffre"),
        );
      }
      if (!special.test(password)) {
        newErrors.push(
          translate(
            "Le mot de passe doit contenir au moins un caractère spécial",
          ),
        );
      }
      if (!length.test(password)) {
        newErrors.push(
          translate("Le mot de passe doit faire au moins 8 caractères de long"),
        );
      }

      // Mise à jour des erreurs
      setPasswordErrors(newErrors);
    }

    // Mettre à jour l'état du mot de passe
    setUserData({
      ...userData,
      motDePasse: password,
    });

    // Style pour les entrées de mot de passe invalides
    const inputStyle = {
      borderColor: newErrors.length > 0 ? "red" : "", // Couleur de la bordure pour les entrées invalides
    };
  };
  // Validation for the confirmation password
  const isConfirmationPasswordValid = (password: string) => {
    return password === userData.motDePasse;
  };

  const handleConfirmationPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const password = e.target.value;
    if (!isConfirmationPasswordValid(password)) {
      // If confirmation password does not match, show an error
      setErrors([translate("Les mots de passe ne correspondent pas")]);
    } else {
      // If confirmation password matches, clear errors
      setErrors([]);
    }
    // Update confirmation password state
    setConfirmationMotDePasse(password);
  };

  const generateRandomPassword = (length: number) => {
    const lowerChars = "abcdefghijklmnopqrstuvwxyz";
    const upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numberChars = "0123456789";
    const specialChars = "[!@#$%^&*?:,;]%~-|+{}()_=<>,";
    const allChars = lowerChars + upperChars + numberChars + specialChars;

    let password = "";

    // Générer au moins un caractère de chaque type
    password += lowerChars[Math.floor(Math.random() * lowerChars.length)];
    password += upperChars[Math.floor(Math.random() * upperChars.length)];
    password += numberChars[Math.floor(Math.random() * numberChars.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    // Générer les caractères restants jusqu'à atteindre la longueur spécifiée
    for (let i = 0; i < length - 4; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Mélanger les caractères du mot de passe
    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    return password;
  };
  const handleGeneratePassword = () => {
    // Logique de génération de mot de passe aléatoire
    const newPassword = generateRandomPassword(passwordLength);
    // Mettre à jour l'état avec le nouveau mot de passe généré
    setGeneratedPassword(newPassword);
    // Afficher le modal
    setShowModal(true);
  };

  const handleCloseModal = () => {
    // Réinitialiser le champ de mot de passe généré
    setGeneratedPassword("");
    // Fermer le modal
    setShowModal(false);
  };

  const handleCopyPassword = () => {
    // Copier le mot de passe dans le presse-papiers
    navigator.clipboard.writeText(generatedPassword);
  };
  const fieldStyle = {
    minHeight: "48px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    boxShadow: "none",
    fontSize: "14px",
    color: "#0f172a",
  } as const;
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "10px",
      borderColor: "#cbd5e1",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#94a3b8",
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 12px",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: "10px",
      overflow: "hidden",
      zIndex: 20,
    }),
  };

  const secondaryButtonStyle = {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#475569",
    borderRadius: "10px",
    padding: "10px 18px",
    fontWeight: 600,
    transition: "all 0.2s ease",
  } as const;

  const warningTextStyle = {
    color: "#ea580c",
    fontSize: "0.875rem",
    marginTop: "0.25rem",
    marginBottom: "6px",
    lineHeight: 1.45,
  } as const;
  return (
    <div className="container-fluid py-4 px-4">
      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "24px 28px",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
          border: "1px solid #eef2f7",
          marginBottom: "24px",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #fff7d6 0%, #ffe58f 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 18px rgba(245, 158, 11, 0.18)",
            }}
          >
            <i
              className="las la-user-edit"
              style={{ fontSize: "28px", color: "#a16207" }}
            ></i>
          </div>

          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "30px",
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {isEditMode ? translate("modify User") : translate("Add User")}
            </h2>
            <p
              style={{
                margin: "6px 0 0 0",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              {isEditMode
                ? translate("Update user information and access")
                : translate("Create a new user account for the platform")}
            </p>
          </div>
        </div>
      </div>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "30px",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
          border: "1px solid #eef2f7",
        }}
      >
        <form>
          <div className="mb-3 row">
            <label
              htmlFor="nom"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Family Name")} * :
            </label>
            <div className="col-lg-9 col-xl-10">
              <input
                type="text"
                className="form-control"
                id="nom"
                name="nom"
                value={userData.nom}
                onChange={handleInputChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="prenom"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("first name")} * :
            </label>
            <div className="col-lg-9 col-xl-10">
              <input
                type="text"
                className="form-control"
                id="prenom"
                name="prenom"
                value={userData.prenom}
                onChange={handleInputChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>
          <div className="mb-3 row">
            <label
              htmlFor="company"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Company")} * :
            </label>
            <div className="col-lg-9 col-xl-10">
              <input
                type="text"
                className="form-control"
                id="company"
                name="company"
                value={userData.company}
                onChange={handleInputChange}
                required
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="phone"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Phone")} :
            </label>

            <div className="col-lg-9 col-xl-10">
              <input
                type="tel"
                className="form-control"
                id="telephone"
                name="telephone"
                value={userData.telephone}
                onChange={handleInputChange}
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="email"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Email")} :
            </label>
            <div className="col-lg-9 col-xl-10">
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={userData.email}
                onChange={handleInputChange}
                style={fieldStyle}
              />
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="username"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("User name")} * :
            </label>

            <div className="col-lg-9 col-xl-10 d-flex flex-column">
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                value={userData.username}
                required
                onChange={handleUsernametChange}
                disabled={isEditMode}
                style={fieldStyle}
              />

              {!isEditMode && userExists && (
                <p
                  id="textindiqusername"
                  style={{ color: "red", fontSize: "12px", marginTop: "6px" }}
                >
                  {translate("Le nom d'utilisateur existe déjà")}
                </p>
              )}

              {!isEditMode && !userExists && userData.username !== "" && (
                <p
                  id="textindiqusername"
                  style={{ color: "green", fontSize: "12px", marginTop: "6px" }}
                >
                  {translate("Nom d'utilisateur accepté")}
                </p>
              )}
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="ville"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Ville")} :
            </label>
            <div className="col-lg-9 col-xl-10">
              <Select
                options={wilayasOptions}
                styles={selectStyles}
                value={
                  selectedWilaya
                    ? wilayasOptions.find(
                        (option) => option.value === selectedWilaya.value,
                      )
                    : null
                }
                onChange={handleWilayaChange}
                placeholder={translate("Sélectionnez une wilaya")}
              />
            </div>
          </div>
          {/*
        <div className="mb-3 row">
          <label htmlFor="ville" className="col-sm-2 col-form-label">
            {translate("Ville")} :
          </label>
          <div className="col-sm-10">
            <Select
              options={wilayasOptions}
              value={
                selectedWilaya
                  ? wilayasOptions.find(
                      (option) => option.value === selectedWilaya.value,
                    )
                  : null
              }
              onChange={handleWilayaChange}
              placeholder={translate("Sélectionnez une wilaya")}
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="affecteA" className="col-sm-2 col-form-label">
            {translate("Affecter à")} :
          </label>
          <div className="col-sm-10">
            <Select
              name="affecteA"
              value={selectedUser}
              onChange={handleUserChange}
              options={affecteAOptions}
              placeholder={translate("User")}
              isClearable
            />
          </div>
        </div>

        <div className="mb-3 row">
          <label htmlFor="profil" className="col-sm-2 col-form-label">
            {translate("Profil")} * :
          </label>
          <div className="col-sm-10">
            <select
              className="form-select"
              id="roles"
              name="roles"
              value={String(userData.roles)}
              onChange={handleSelectChange}
              required
              disabled={
                isEditMode ||
                (!isEditMode && requestedRole?.toLowerCase() === "supplier")
              }
        */}
          <div className="mb-3 row">
            <label
              htmlFor="affecteA"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Affecter à")} :
            </label>
            <div className="col-lg-9 col-xl-10">
              <Select
                name="affecteA"
                styles={selectStyles}
                value={selectedUser}
                onChange={handleUserChange}
                options={affecteAOptions}
                placeholder={translate("User")}
                isClearable
              />
            </div>
          </div>

          <div className="mb-3 row">
            <label
              htmlFor="profil"
              className="col-lg-3 col-xl-2 col-form-label"
              style={{ color: "#0f172a", fontWeight: 600 }}
            >
              {translate("Profil")} * :
            </label>
            <div className="col-lg-9 col-xl-10">
              <select
                className="form-select"
                id="roles"
                name="roles"
                value={userData.roles}
                onChange={handleSelectChange}
                required
                disabled={
                  !isEditMode &&
                  (requestedRole?.toLowerCase() === "supplier" ||
                    requestedRole?.toLowerCase() === "oem" ||
                    requestedRole?.toLowerCase() === "expert")
                }
                style={fieldStyle}
              >
                <option value="0"> {translate("Select a role")}</option>
                {rolesOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isEditMode && (
            <div className="mb-3 row">
              <div className="col-lg-3 col-xl-2"></div>
              <div className="col-lg-9 col-xl-10">
                <button
                  type="button"
                  onClick={() => setChangePassword(!changePassword)}
                  style={{
                    background: changePassword ? "#fee2e2" : "#eff6ff",
                    border: `1px solid ${changePassword ? "#fca5a5" : "#93c5fd"}`,
                    color: changePassword ? "#b91c1c" : "#1d4ed8",
                    borderRadius: "10px",
                    padding: "8px 14px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <i
                    className={`las ${changePassword ? "la-times" : "la-key"}`}
                  ></i>
                  {changePassword
                    ? translate("Cancel")
                    : translate("Change password")}
                </button>
              </div>
            </div>
          )}
          {isEditMode && changePassword && (
            <div>
              <br />
              <>
                {/* Bouton pour ouvrir le modal */}
                <Button
                  onClick={() => setShowModal(true)}
                  style={{
                    background: "#0f172a",
                    border: "1px solid #0f172a",
                    color: "#ffffff",
                    height: "46px",
                    minWidth: "180px",
                    borderRadius: "10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  title={translate("Generate Password")}
                >
                  <i className="las la-lock"></i>
                  {translate("Generate Password")}
                </Button>

                {/* Modal pour afficher le mot de passe généré */}
                <Modal show={showModal} onHide={handleCloseModal} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>{translate("Generate Password")}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Row className="align-items-center">
                      <Col Col xs="auto">
                        <Form.Group controlId="generatedPassword">
                          <Form.Control
                            type="text"
                            readOnly
                            value={generatedPassword}
                            style={fieldStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col className="col-3">
                        <Form.Group controlId="passwordLength">
                          <Form.Control
                            type="number"
                            value={passwordLength}
                            onChange={(e) =>
                              setPasswordLength(Number(e.target.value))
                            }
                            style={fieldStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Button
                          onClick={handleCopyPassword}
                          style={secondaryButtonStyle}
                        >
                          {translate("Copy")}
                        </Button>
                      </Col>
                    </Row>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      onClick={handleGeneratePassword}
                      style={{
                        background:
                          "linear-gradient(135deg, #facc15 0%, #facc15 100%)",
                        border: "none",
                        color: "#111827",
                        borderRadius: "10px",
                        padding: "8px 16px",
                        fontWeight: 700,
                      }}
                    >
                      {translate("Generate Password")}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </>
              {/* Votre formulaire de modification de mot de passe ici */}
              <div className="mb-3 row">
                <label
                  htmlFor="motDePasse"
                  className="col-lg-3 col-xl-2 col-form-label"
                  style={{ color: "#0f172a", fontWeight: 600 }}
                >
                  {translate("Mot de passe")} * :
                </label>
                <div className="col-lg-9 col-xl-10 position-relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    id="motDePasse"
                    name="motDePasse"
                    value={userData.motDePasse}
                    onChange={handlePasswordChange}
                    required
                    style={fieldStyle}
                  />
                  <span
                    className={`password-toggle-icon ${
                      showPassword ? "visible" : ""
                    }`}
                    style={{
                      position: "absolute",
                      right: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IoEyeOff /> : <IoEye />}
                  </span>
                </div>
              </div>
              <div className="mb-3 row">
                <label
                  htmlFor="confirmationMotDePasse"
                  className="col-lg-3 col-xl-2 col-form-label"
                  style={{ color: "#0f172a", fontWeight: 600 }}
                >
                  {translate("Confirmer le mot de passe *")}
                </label>
                <div className="col-lg-9 col-xl-10">
                  <input
                    type="password"
                    className={`form-control ${
                      userData.motDePasse !== confirmationMotDePasse
                        ? "is-invalid"
                        : ""
                    }`}
                    id="confirmationMotDePasse"
                    name="confirmationMotDePasse"
                    value={confirmationMotDePasse}
                    onChange={(e) => setConfirmationMotDePasse(e.target.value)}
                    required
                    style={fieldStyle}
                  />

                  {userData.motDePasse !== confirmationMotDePasse && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {translate("Les mots de passe ne correspondent pas")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isEditMode && (
            <div>
              <br />
              <>
                {/* Bouton pour ouvrir le modal */}
                <Button
                  onClick={() => setShowModal(true)}
                  style={{
                    background: "#0f172a",
                    border: "1px solid #0f172a",
                    color: "#ffffff",
                    height: "46px",
                    minWidth: "180px",
                    borderRadius: "10px",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  title={translate("Generate Password")}
                >
                  <i className="las la-lock"></i>
                  {translate("Generate Password")}
                </Button>

                {/* Modal pour afficher le mot de passe généré */}
                <Modal show={showModal} onHide={handleCloseModal} centered>
                  <Modal.Header closeButton>
                    <Modal.Title>{translate("Generate Password")}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Row className="align-items-center">
                      <Col Col xs="auto">
                        <Form.Group controlId="generatedPassword">
                          <Form.Control
                            type="text"
                            readOnly
                            value={generatedPassword}
                            style={fieldStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col className="col-3">
                        <Form.Group controlId="passwordLength">
                          <Form.Control
                            type="number"
                            value={passwordLength}
                            onChange={(e) =>
                              setPasswordLength(Number(e.target.value))
                            }
                            style={fieldStyle}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Button
                          onClick={handleCopyPassword}
                          style={secondaryButtonStyle}
                        >
                          {translate("Copy")}
                        </Button>
                      </Col>
                    </Row>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      onClick={handleGeneratePassword}
                      style={{
                        background:
                          "linear-gradient(135deg, #facc15 0%, #facc15 100%)",
                        border: "none",
                        color: "#111827",
                        borderRadius: "10px",
                        padding: "8px 16px",
                        fontWeight: 700,
                      }}
                    >
                      {translate("Generate Password")}
                    </Button>
                  </Modal.Footer>
                </Modal>
              </>

              {/* Password Input */}
              <div className="mb-3 row">
                <label
                  htmlFor="motDePasse"
                  className="col-lg-3 col-xl-2 col-form-label"
                  style={{ color: "#0f172a", fontWeight: 600 }}
                >
                  {translate("Mot de passe")} * :
                </label>
                <div className="col-lg-9 col-xl-10 position-relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    id="motDePasse"
                    name="motDePasse"
                    value={userData.motDePasse}
                    onChange={handlePasswordChange}
                    required
                    style={fieldStyle}
                  />
                  {/* Password toggle icon */}
                  <span
                    className={`password-toggle-icon ${
                      showPassword ? "visible" : ""
                    }`}
                    style={{
                      position: "absolute",
                      right: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IoEyeOff /> : <IoEye />}
                  </span>
                </div>
              </div>

              {/* Confirmation Password Input */}
              <div className="mb-3 row">
                <label
                  htmlFor="confirmationMotDePasse"
                  className="col-lg-3 col-xl-2 col-form-label"
                  style={{ color: "#0f172a", fontWeight: 600 }}
                >
                  {translate("Confirmer le mot de passe *")}
                </label>
                <div className="col-lg-9 col-xl-10">
                  <input
                    type="password"
                    className={`form-control ${
                      userData.motDePasse !== confirmationMotDePasse
                        ? "is-invalid"
                        : ""
                    }`}
                    id="confirmationMotDePasse"
                    name="confirmationMotDePasse"
                    value={confirmationMotDePasse}
                    onChange={handleConfirmationPasswordChange}
                    required
                    style={fieldStyle}
                  />
                  {/* Error message for mismatched confirmation password */}
                  {userData.motDePasse !== confirmationMotDePasse && (
                    <p className="invalid-feedback">
                      {translate("Les mots de passe ne correspondent pas")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* <div className="mb-3 row">
          <label htmlFor="valideJusqua" className="col-lg-3 col-xl-2 col-form-label"
style={{ color: "#0f172a", fontWeight: 600 }}>
            {translate("Valid until")}
          </label>
          <div className="col-lg-9 col-xl-10">
            <input
              type="date"
              className="form-control"
              id="valideJusqua"
              name="valideJusqua"
              value={userData.valideJusqua}
              onChange={handleInputChange}
              style={fieldStyle}
            />
          </div>
        </div> */}
          {requiredFieldError && (
            <div className="mb-3 row">
              <div className="col-lg-3 col-xl-2"></div>
              <div
                className="col-lg-9 col-xl-10"
                style={{ color: "#dc2626", fontSize: "0.875rem" }}
              >
                {requiredFieldError}
              </div>
            </div>
          )}
          <div className="mb-3 row">
            <div className="col-lg-3 col-xl-2"></div>
            <div className="col-lg-9 col-xl-10">
              {errors.map((error, index) => (
                <p
                  key={index}
                  style={{
                    color: "#dc2626",
                    fontSize: "0.875rem",
                    marginTop: "0.25rem",
                    marginBottom: "6px",
                  }}
                >
                  {error}
                </p>
              ))}

              {passwordErrors.length > 0 && (
                <div>
                  {passwordErrors.map((error, index) => (
                    <p key={index} style={warningTextStyle}>
                      <i className="las la-exclamation-triangle mr-1"></i>
                      {error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mb-3 row">
            <div className="col-12 d-flex justify-content-between mt-4">
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  background: "#64748b",
                  border: "1px solid #64748b",
                  color: "#ffffff",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="las la-times"></i>
                {translate("Cancel")}
              </button>

              <button
                id="saveuser"
                type="button"
                onClick={handleOpenConfirmationModal}
                style={{
                  background:
                    "linear-gradient(135deg, #facc15 0%, #facc15 100%)",
                  border: "none",
                  color: "#ffffff",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontWeight: 700,
                  boxShadow: "0 10px 24px rgba(34, 197, 94, 0.22)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i
                  className={`las ${isEditMode ? "la-edit" : "la-user-plus"}`}
                ></i>
                {isEditMode ? translate("modify User") : translate("Add User")}
              </button>
            </div>

            <Modal
              show={showConfirmationModal}
              onHide={() => setShowConfirmationModal(false)}
              backdrop="static"
              keyboard={false}
              centered
            >
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "18px",
                  overflow: "hidden",
                  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                }}
              >
                <Modal.Header
                  closeButton
                  style={{
                    borderBottom: "1px solid #eef2f7",
                    padding: "24px 28px 18px 28px",
                  }}
                >
                  <Modal.Title
                    style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {isEditMode
                      ? translate("Confirmation of modification.")
                      : translate("Confirmation of add.")}
                  </Modal.Title>
                </Modal.Header>

                <Modal.Body
                  style={{
                    fontSize: "15px",
                    color: "#475569",
                    padding: "24px 28px",
                  }}
                >
                  {isEditMode
                    ? translate("Are you sure you want to modify this user?")
                    : translate("Are you sure you want to add this user?")}
                </Modal.Body>

                <Modal.Footer
                  style={{
                    borderTop: "1px solid #eef2f7",
                    padding: "18px 28px 24px 28px",
                  }}
                >
                  <Button
                    onClick={() => setShowConfirmationModal(false)}
                    style={{
                      background: "#64748b",
                      border: "1px solid #64748b",
                      color: "#ffffff",
                      borderRadius: "10px",
                      padding: "8px 16px",
                      fontWeight: 600,
                    }}
                  >
                    {translate("Cancel")}
                  </Button>
                  <Button
                    onClick={handleConfirmUpdate}
                    style={{
                      background:
                        "linear-gradient(135deg, #facc15 0%, #facc15 100%)",
                      border: "none",
                      color: "#111827",
                      borderRadius: "10px",
                      padding: "8px 16px",
                      fontWeight: 700,
                    }}
                  >
                    {translate("Confirm")}
                  </Button>
                </Modal.Footer>
              </div>
            </Modal>
          </div>
        </form>
      </div>
    </div>
  );
};

export default User;
